import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, RefreshCw, Search, Star } from 'lucide-react';
import clsx from 'clsx';

import PageHeader from '@/components/PageHeader';
import {
  listAppReviews,
  setAppReviewHidden,
  type AppReviewItem,
  type AppReviewsQuery,
} from '@/lib/operatorApi';
import { formatDateTime, formatKop } from '@/lib/format';

// Модерация отзывов гостей. Отзыв всегда привязан к завершённому заказу,
// поэтому «накрутить» его нельзя; оператор может только скрыть — тогда он
// исчезает из приложения и не учитывается в рейтинге заведения.

const PAGE = 50;

export default function AppReviews() {
  const [q, setQ] = useState('');
  const [hidden, setHidden] = useState<NonNullable<AppReviewsQuery['hidden']>>('all');
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<AppReviewItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listAppReviews({ q: q.trim() || undefined, hidden, limit: PAGE, offset })
      .then((r) => {
        setItems(r.results);
        setTotal(r.total);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [q, hidden, offset]);

  // Поиск — с задержкой, чтобы не дёргать бэкенд на каждую букву.
  useEffect(() => {
    const t = window.setTimeout(load, 250);
    return () => window.clearTimeout(t);
  }, [load]);

  const toggle = async (r: AppReviewItem) => {
    try {
      setBusyId(r.id);
      await setAppReviewHidden(r.id, !r.isHidden);
      setItems((list) => list.map((x) => (x.id === r.id ? { ...x, isHidden: !r.isHidden } : x)));
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setBusyId(null);
    }
  };

  const pageFrom = total === 0 ? 0 : offset + 1;
  const pageTo = Math.min(offset + PAGE, total);

  return (
    <div>
      <PageHeader
        eyebrow="Гостевое приложение"
        title="Отзывы"
        description="Отзывы гостей к завершённым заказам. Скрытый отзыв не показывается в приложении и не влияет на рейтинг заведения."
        actions={
          <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
            <RefreshCw size={13} strokeWidth={1.75} />
            Обновить
          </button>
        }
      />

      <div className="page-pad">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center">
          <label className="relative flex-1">
            <Search size={14} strokeWidth={1.75} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
            <input
              className="input !pl-9"
              placeholder="Текст отзыва или заведение"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setOffset(0);
              }}
            />
          </label>
          <div className="flex gap-1">
            {(
              [
                ['all', 'Все'],
                ['visible', 'Видимые'],
                ['hidden', 'Скрытые'],
              ] as const
            ).map(([v, label]) => (
              <button
                key={v}
                type="button"
                className={clsx('btn !px-3 !py-1.5 text-[12px]', hidden === v ? 'bg-ink text-white' : 'btn-outline')}
                onClick={() => {
                  setHidden(v);
                  setOffset(0);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-4 border border-red-600/30 bg-red-50 px-4 py-2 text-[13px] text-red-700">
            {error}
          </div>
        )}

        {loading && items.length === 0 ? (
          <div className="text-ink/50">Загрузка…</div>
        ) : items.length === 0 ? (
          <div className="card p-10 text-center text-[14px] text-ink/50">
            Отзывов не найдено.
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((r) => {
              const busy = busyId === r.id;
              return (
                <li key={r.id} className={clsx('card p-4', busy && 'opacity-60', r.isHidden && 'bg-paper')}>
                  <div className="flex flex-wrap items-start gap-3">
                    <Stars rating={r.rating} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="text-[14px] font-medium">{r.authorName || 'Гость'}</span>
                        <span className="text-[12px] text-ink/50">{formatDateTime(r.createdAt)}</span>
                        <span className="text-[12px] text-ink/50">·</span>
                        <Link to={`/tenants/${r.establishment.id}`} className="link text-[12px]">
                          {r.establishment.name}
                        </Link>
                        {r.order && (
                          <span className="text-[12px] text-ink/50">
                            · заказ №{r.order.number}, {formatKop(r.order.total)}
                          </span>
                        )}
                        {r.isHidden && <span className="badge-ink">Скрыт</span>}
                      </div>
                      {r.text ? (
                        <p className="mt-1.5 whitespace-pre-line text-[14px] leading-relaxed text-ink/90">{r.text}</p>
                      ) : (
                        <p className="mt-1.5 text-[13px] italic text-ink/40">Без текста — только оценка</p>
                      )}
                      <div className="mt-2 text-[12px] text-ink/50">
                        {[r.client.name, r.client.email, r.client.phone].filter(Boolean).join(' · ') || r.client.id}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={r.isHidden ? 'btn-outline' : 'btn-ghost text-red-600'}
                      onClick={() => void toggle(r)}
                      disabled={busy}
                    >
                      {r.isHidden ? (
                        <>
                          <Eye size={14} strokeWidth={1.75} /> Показать
                        </>
                      ) : (
                        <>
                          <EyeOff size={14} strokeWidth={1.75} /> Скрыть
                        </>
                      )}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {total > PAGE && (
          <div className="mt-4 flex items-center justify-between text-[13px] text-ink/60">
            <span>
              {pageFrom}–{pageTo} из {total}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-outline"
                onClick={() => setOffset((o) => Math.max(0, o - PAGE))}
                disabled={offset === 0 || loading}
              >
                Назад
              </button>
              <button
                type="button"
                className="btn-outline"
                onClick={() => setOffset((o) => o + PAGE)}
                disabled={offset + PAGE >= total || loading}
              >
                Дальше
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex shrink-0 items-center gap-0.5" aria-label={`Оценка ${rating} из 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={14}
          strokeWidth={1.75}
          className={n <= rating ? 'fill-amber-400 text-amber-400' : 'text-ink/20'}
        />
      ))}
    </div>
  );
}
